import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import { Row, Col, Alert, Card, CardBody, Container, FormFeedback, Input, Label, Form, Spinner } from "reactstrap";

//redux
import { useSelector, useDispatch } from "react-redux";

import { Link, useNavigate } from "react-router-dom";
import withRouter from "../../Components/Common/withRouter";

// Formik Validation
import * as Yup from "yup";
import { useFormik } from "formik";

// action
// NOTE: userForgetPassword is for requesting a reset. A new action for performing reset is needed.
import { userForgetPassword, /*, userPerformResetPassword */ 
userPerformResetPassword} from "../../slices/thunks";

// import images
// import profile from "../../assets/images/bg.png";
import logoLight from "../../assets/images/Logo-with.png";
import ParticlesAuth from "../AuthenticationInner/ParticlesAuth";
import CryptoJS from 'crypto-js';
import { createSelector } from "reselect";
// Add this import
import { useTranslation } from "react-i18next";

// Helper function to get client IP (consider moving to a utility file)
async function getClientIp() {
  try {
    const response = await fetch('https://api.ipify.org?format=json');
    const data = await response.json();
    return data.ip;
  } catch (error)
 {
    console.error('Failed to get IP:', error);
    return 'IP_FETCH_ERROR'; // Or handle more gracefully
  }
}

const ResetPasswordPage = props => {
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { token: encryptedTokenFromUrl } = props.router.params;
  const [tokenIsValid, setTokenIsValid] = useState(false);
  const [tokenErrorMsg, setTokenErrorMsg] = useState('');
  const [validatedEmail, setValidatedEmail] = useState('');
  const [passwordShow, setPasswordShow] = useState(false);
  const [confirmPasswordShow, setConfirmPasswordShow] = useState(false);

  const validation = useFormik({
    enableReinitialize: true,

    initialValues: {
      email: '',
      password: '',
      confirmPassword: '',
    },
    validationSchema: Yup.object({
      email: Yup.string().email(t("Invalid email format")).required(t("Email is required")),
      password: Yup.string()
        .min(6, t("Password must be at least 6 characters"))
        .required(t("Password is required")),
      confirmPassword: Yup.string()
        .oneOf([Yup.ref('password'), null], t("Passwords must match"))
        .required(t("Confirm Password is required")),
    }),
    onSubmit: (values) => {
      if (tokenIsValid && validatedEmail) {
        dispatch(userPerformResetPassword({ email: validatedEmail, newPassword: values.confirmPassword }));
      } else {
        setTokenErrorMsg(t("The password reset link is invalid. Please request a new one."));
        console.error("Form submitted with an invalid or unvalidated token.");
      }
    }
  });

  const { setFieldValue } = validation;

  useEffect(() => {
    const validateToken = async () => {
      if (encryptedTokenFromUrl) {
        try {
          const secretKey = "FORGETPASS"; 
          const decodedEncryptedToken = decodeURIComponent(encryptedTokenFromUrl);
          const bytes = CryptoJS.AES.decrypt(decodedEncryptedToken, secretKey);
          const decryptedDataString = bytes.toString(CryptoJS.enc.Utf8);

          if (!decryptedDataString) {
            throw new Error("Decryption resulted in empty data.");
          }

          const payload = JSON.parse(decryptedDataString);

          if (!payload || typeof payload.email !== 'string' || typeof payload.ipAddress !== 'string' ||
              typeof payload.userAgent !== 'string' || typeof payload.expiresAt !== 'string') {
            throw new Error("Invalid token payload structure.");
          }

          const currentIp = await getClientIp();
          const currentUserAgent = navigator.userAgent;

          const isIpMatch = payload.ipAddress === currentIp;
          const isUserAgentMatch = payload.userAgent === currentUserAgent;
          const isNotExpired = new Date() < new Date(payload.expiresAt);
          
          if (isIpMatch && isUserAgentMatch && isNotExpired) {
            setFieldValue('email', payload.email);
            setValidatedEmail(payload.email);
            setTokenIsValid(true);
            setTokenErrorMsg('');
          } else {
            let specificError = t("The password reset link is invalid or has expired.");
            if (!isNotExpired) specificError = t("PasswordResetExpiredErroMessage");
            else if (!isIpMatch || !isUserAgentMatch) specificError = t("This reset link is not valid for your current device or network.");
            setTokenErrorMsg(specificError);
            setFieldValue('email', t("Invalid token"));
            setTokenIsValid(false);
            // Redirect to login
            setTimeout(() => navigate('/login'), 2000); 
          }
        } catch (e) {
          console.error("Token validation/decryption failed:", e);
          setTokenErrorMsg(t("The password reset link is invalid or has expired. Critical error."));
          setFieldValue('email', t("Invalid token"));
          setTokenIsValid(false);
          // Redirect to login
          setTimeout(() => navigate('/login'), 2000); 
        }
      }
    };
    validateToken();
  }, [encryptedTokenFromUrl, t, setFieldValue, navigate]);

  const selectLayoutState = (state) => state.ForgetPassword;
  const selectLayoutProperties = createSelector(
    selectLayoutState,
    (state) => ({
      resetPasswordLoading: state.resetPasswordLoading,
      resetPasswordError: state.resetPasswordError, 
      resetPasswordSuccessMsg: state.resetPasswordSuccessMsg, 
    })
  );

  // Inside your component
  const {
    resetPasswordLoading , resetPasswordError, resetPasswordSuccessMsg
  } = useSelector(selectLayoutProperties);

  document.title = "Alitqan |" + t("Set New Password") ;
  
  return (
    <ParticlesAuth>
      <div className="auth-page-content mt-lg-5">

        <Container>
          <Row>
            <Col lg={12}>
              <div className="text-center mt-sm-5 mb-4 text-white-50">
                <div>
                  <Link to="/" className="d-inline-block auth-logo">
                     <img src={logoLight} alt="" height="50" />
                  </Link>
                </div>
                {/* <p className="mt-3 fs-15 fw-medium">Premium Admin & Dashboard Template</p> */}
              </div>
            </Col>
          </Row>

          <Row className="justify-content-center">
            <Col md={8} lg={6} xl={5}>
              <Card className="mt-4">

                <CardBody className="p-4">
                  <div className="text-center mt-2">
                    <h5 className="text-primary">{t("Set New Password")}</h5>
                    <p className="text-muted">{t("Create a new password for your account.")}</p>
                  </div>

                  {tokenErrorMsg && (
                    <Alert color="danger" className="text-center mb-2 mx-2" role="alert">
                      {t(tokenErrorMsg)}
                    </Alert>
                  )}
                  <div className="p-2">
                    {resetPasswordError ? (
                      <Alert color="danger" style={{ marginTop: "13px" }}>
                        {t(resetPasswordError)}
                      </Alert>
                    ) : null}
                    {resetPasswordSuccessMsg  ? (
                      <Alert color="success" style={{ marginTop: "13px" }}>
                        {t(resetPasswordSuccessMsg)}
                      </Alert>
                    ) : null}
                    <Form
                      dir={i18n.language === "ar" ? "rtl" : "ltr"}
                      onSubmit={(e) => {
                        e.preventDefault();
                        validation.handleSubmit();
                        return false;
                      }}
                    >
                      <div className="mb-4">
                        <Label className="form-label">{t("Email")}</Label>
                        <Input
                          name="email"
                          dir={i18n.language === "ar" ? "rtl" : "ltr"}
                          className={`form-control`}
                          placeholder={t("Enter email")}
                          disabled // Email field is disabled
                          type="email"
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          value={validation.values.email || ""}
                          readOnly // Ensure it's not editable
                          invalid={
                            validation.touched.email && validation.errors.email ? true : false
                          }
                        />
                        {validation.touched.email && validation.errors.email ? (
                          <FormFeedback type="invalid"><div>{validation.errors.email}</div></FormFeedback>
                        ) : null}
                      </div>

                      <div className="mb-3">
                        <Label className="form-label" htmlFor="password-input">{t("New Password")}</Label>
                          <div className="position-relative auth-pass-inputgroup mb-3">
                            <Input
                              name="password"
                              value={validation.values.password || ""}
                              type={passwordShow ? "text" : "password"}
                              className="form-control"
                              placeholder={t("enterPassword")}
                              onChange={validation.handleChange}
                              onBlur={validation.handleBlur}
                              invalid={
                                validation.touched.password && validation.errors.password
                                  ? true
                                  : false
                              }
                            />
                            {validation.touched.password && validation.errors.password ? (
                              <FormFeedback type="invalid">
                                {validation.errors.password}
                              </FormFeedback>
                            ) : null}
                            <button
                              className={`btn btn-link position-absolute ${
                                i18n.language === "ar" ? "start-0" : "end-0"
                              } top-0 text-decoration-none text-muted material-shadow-none`}
                              type="button"
                              id="password-addon"
                              onClick={() => setPasswordShow(!passwordShow)}
                            >
                              <i className="ri-eye-fill align-middle"></i>
                            </button>
                          </div>
                      </div>

                      <div className="mb-3">
                        <Label className="form-label" htmlFor="confirm-password-input">{t("Confirm Password")}</Label>
                          <div className="position-relative auth-pass-inputgroup mb-3">
                            <Input
                              name="confirmPassword"
                              value={validation.values.confirmPassword || ""}
                              type={confirmPasswordShow ? "text" : "password"}
                              className="form-control"
                              placeholder={t("Enter confirm password")}
                              onChange={validation.handleChange}
                              onBlur={validation.handleBlur}
                              invalid={
                                validation.touched.confirmPassword && validation.errors.confirmPassword
                                  ? true
                                  : false
                              }
                            />
                            {validation.touched.confirmPassword && validation.errors.confirmPassword ? (
                              <FormFeedback type="invalid">
                                {validation.errors.confirmPassword}
                              </FormFeedback>
                            ) : null}
                            <button
                              className={`btn btn-link position-absolute ${
                                i18n.language === "ar" ? "start-0" : "end-0"
                              } top-0 text-decoration-none text-muted material-shadow-none`}
                              type="button"
                              id="confirm-password-addon"
                              onClick={() => setConfirmPasswordShow(!confirmPasswordShow)}
                            >
                              <i className="ri-eye-fill align-middle"></i>
                            </button>
                          </div>
                      </div>

                      <div className="text-center mt-4">
                        <button className="btn btn-success w-100" type="submit" disabled={resetPasswordLoading || !tokenIsValid}>
                          {resetPasswordLoading   ? (
                            <Spinner size="sm" className="me-2 ms-2">
                              {" "}
                              Loading...{" "}
                            </Spinner>
                          ) : null}
                          {t("Reset Password")}
                        </button>
                      </div>
                    </Form>
                  </div>
                </CardBody>
              </Card>

              <div className="mt-4 text-center">
                <p className="mb-0">{t("Remembered your password?")} <Link to="/login" className="fw-semibold text-primary text-decoration-underline"> {t("Click here to Login")} </Link> </p>
              </div>

            </Col>
          </Row>
        </Container>
      </div>
    </ParticlesAuth>
  );
};

ResetPasswordPage.propTypes = {
  router: PropTypes.object, // Updated from history due to withRouter
};

export default withRouter(ResetPasswordPage);
